import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from '../utils/axiosInstance';
import {
  Row,
  Col,
  Card,
  Form,
  Alert,
  Spinner,
  InputGroup,
  Badge,
  Modal,
  OverlayTrigger,
  Tooltip,
  Pagination,
} from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

import { getApiBase } from "../utils/useApiBase";
const API_URL = `${getApiBase()}/umutekano`;

const GutangaUmutekano = () => {
  // ════════════════════════════════════════════════════
  // STATES
  // ════════════════════════════════════════════════════
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState({ text: '', variant: '' });
  const [formData, setFormData] = useState({
    amazina_y_umuturage: '',
    utanga_umutekano: 'Yego',
    utanga_angahe: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const [windowHeight, setWindowHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 800
  );
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const fabRef = useRef(null);

  const config = {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  };

  // ════════════════════════════════════════════════════
  // DEVICE DETECTION
  // ════════════════════════════════════════════════════
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    const handleOrientation = () => setTimeout(handleResize, 100);
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientation);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientation);
    };
  }, []);

  // Close FAB on outside click
  useEffect(() => {
    const handler = (e) => {
      if (fabRef.current && !fabRef.current.contains(e.target)) {
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('touchstart', handler);
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('mousedown', handler);
    };
  }, []);

  const device = useMemo(() => {
    const w = windowWidth;
    return {
      isFoldOuter: w < 300,
      isXSmall: w >= 300 && w < 360,
      isSmall: w >= 360 && w < 390,
      isMedium: w >= 390 && w < 414,
      isLarge: w >= 414 && w < 480,
      isPhablet: w >= 480 && w < 576,
      isMobile: w < 576,
      isTabletSm: w >= 576 && w < 768,
      isTabletMd: w >= 768 && w < 992,
      isTablet: w >= 576 && w < 992,
      isDesktopSm: w >= 992 && w < 1200,
      isDesktop: w >= 992,
      isDesktopLg: w >= 1200 && w < 1400,
      isDesktopXl: w >= 1400,
      isLandscapePhone: w > 576 && w < 900 && windowHeight < 500,
    };
  }, [windowWidth, windowHeight]);

  const itemsPerPage = useMemo(() => {
    if (device.isFoldOuter) return 4;
    if (device.isXSmall) return 5;
    if (device.isMobile) return 6;
    if (device.isTabletSm) return 8;
    if (device.isTabletMd) return 10;
    return 12;
  }, [device]);

  // ════════════════════════════════════════════════════
  // THEME
  // ════════════════════════════════════════════════════
  const theme = {
    bg: "#f0f4f8",
    bgSecondary: "#eef3fb",
    card: "#ffffff",
    text: "#374151",
    textBright: "#0d1b2a",
    muted: "#64748b",
    primary: "#0B3D91",
    primaryGlow: "rgba(11,61,145,0.18)",
    success: "#16a34a",
    successGlow: "rgba(22,163,74,0.18)",
    warning: "#d97706",
    warningGlow: "rgba(217,119,6,0.18)",
    danger: "#dc2626",
    dangerGlow: "rgba(220,38,38,0.18)",
    info: "#0369a1",
    infoGlow: "rgba(3,105,161,0.18)",
    border: "#e2e8f0",
    borderLight: "#f1f5f9",
    gradient1: "linear-gradient(135deg, #0B3D91 0%, #093278 100%)",
    gradient2: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
    gradient3: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
    gradient4: "linear-gradient(135deg, #0369a1 0%, #075985 100%)",
  };

  // ════════════════════════════════════════════════════
  // RESPONSIVE SIZES
  // ════════════════════════════════════════════════════
  const rs = useMemo(() => ({
    headerTitle: device.isFoldOuter ? '0.85rem' : device.isXSmall ? '0.95rem'
      : device.isMobile ? '1.05rem' : device.isTablet ? '1.35rem' : '1.75rem',
    headerSub: device.isMobile ? '0.62rem' : device.isTablet ? '0.72rem' : '0.82rem',
    statValue: device.isFoldOuter ? '1.1rem' : device.isXSmall ? '1.25rem'
      : device.isSmall ? '1.4rem' : device.isMedium ? '1.55rem'
      : device.isLarge ? '1.7rem' : device.isPhablet ? '1.8rem'
      : device.isTabletSm ? '1.9rem' : device.isTabletMd ? '2.1rem'
      : device.isDesktopSm ? '2.2rem' : '2.4rem',
    statTitle: device.isFoldOuter ? '0.48rem' : device.isXSmall ? '0.52rem'
      : device.isMobile ? '0.6rem' : device.isTablet ? '0.72rem' : '0.85rem',
    iconSize: device.isFoldOuter ? 36 : device.isXSmall ? 40 : device.isMobile ? 44
      : device.isTablet ? 50 : 56,
    cardPad: device.isFoldOuter ? '0.6rem' : device.isXSmall ? '0.7rem'
      : device.isMobile ? '0.85rem' : device.isTablet ? '1.2rem' : '1.5rem',
    cardRadius: device.isFoldOuter ? '12px' : device.isMobile ? '14px'
      : device.isTablet ? '16px' : '20px',
    containerPad: device.isFoldOuter ? '0.35rem' : device.isXSmall ? '0.5rem'
      : device.isMobile ? '0.6rem' : device.isTablet ? '1rem' : '1.5rem',
    badgeFont: device.isFoldOuter ? '0.5rem' : device.isXSmall ? '0.55rem'
      : device.isMobile ? '0.62rem' : '0.72rem',
    actionBtn: device.isFoldOuter ? 30 : device.isXSmall ? 32
      : device.isMobile ? 36 : device.isTablet ? 36 : 38,
    modalTitle: device.isMobile ? '0.92rem' : device.isTablet ? '1.05rem' : '1.2rem',
    formLabel: device.isMobile ? '0.78rem' : '0.85rem',
    formPad: device.isMobile ? '0.55rem 0.7rem' : '0.65rem 0.9rem',
  }), [device]);

  // ════════════════════════════════════════════════════
  // DATA LOGIC
  // ════════════════════════════════════════════════════
  const showMsg = useCallback((text, variant) => {
    setMessage({ text, variant });
    setTimeout(() => setMessage({ text: '', variant: '' }), 4000);
  }, []);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_URL, config);
      setData(res.data || []);
    } catch {
      const demo = [
        { id: 1, amazina_y_umuturage: "Kalisa Jean", utanga_umutekano: "Yego", utanga_angahe: 500, created_at: new Date().toISOString() },
        { id: 2, amazina_y_umuturage: "Uwase Sarah", utanga_umutekano: "Oya", utanga_angahe: 0, created_at: new Date().toISOString() },
        { id: 3, amazina_y_umuturage: "Mugabo Pierre", utanga_umutekano: "Yego", utanga_angahe: 1000, created_at: new Date().toISOString() },
        { id: 4, amazina_y_umuturage: "Ingabire Claire", utanga_umutekano: "Yego", utanga_angahe: 750, created_at: new Date().toISOString() },
        { id: 5, amazina_y_umuturage: "Habimana Eric", utanga_umutekano: "Oya", utanga_angahe: 0, created_at: new Date().toISOString() },
        { id: 6, amazina_y_umuturage: "Mukamana Grace", utanga_umutekano: "Yego", utanga_angahe: 300, created_at: new Date().toISOString() },
      ];
      setData(demo);
      showMsg("Demo Mode: API ntikunda kugerwa.", "warning");
    } finally {
      setLoading(false);
    }
  }, [showMsg]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    // Enforce: Oya always saves 0
    const payload = {
      ...formData,
      utanga_angahe: formData.utanga_umutekano === 'Oya' ? 0 : (parseFloat(formData.utanga_angahe) || 0),
    };
    try {
      if (editingId) {
        await axios.put(`${API_URL}/${editingId}`, payload, config);
        showMsg("Amakuru yavuguruwe neza!", "success");
      } else {
        await axios.post(API_URL, payload, config);
        showMsg("Amakuru yabitswe neza!", "success");
      }
      resetForm();
      fetchRecords();
    } catch {
      showMsg("Habaye ikosa mu kubika amakuru", "danger");
    }
  }, [editingId, formData, showMsg, fetchRecords]);

  const handleEdit = useCallback((item) => {
    setEditingId(item.id);
    const isOya = item.utanga_umutekano === 'Oya';
    setFormData({
      amazina_y_umuturage: item.amazina_y_umuturage,
      utanga_umutekano: item.utanga_umutekano,
      utanga_angahe: isOya ? 0 : (item.utanga_angahe || ''),
    });
    setShowForm(true);
    if (device.isMobile) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [device]);

  const handleDeleteClick = useCallback((item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!itemToDelete) return;
    try {
      await axios.delete(`${API_URL}/${itemToDelete.id}`, config);
      showMsg("Amakuru yasibwe neza!", "success");
      fetchRecords();
    } catch {
      showMsg("Habaye ikosa mu gusiba", "danger");
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  }, [itemToDelete, showMsg, fetchRecords]);

  const resetForm = useCallback(() => {
    setFormData({ amazina_y_umuturage: '', utanga_umutekano: 'Yego', utanga_angahe: '' });
    setEditingId(null);
    setShowForm(false);
  }, []);

  const filteredData = useMemo(() =>
    data.filter(item =>
      item.amazina_y_umuturage?.toLowerCase().includes(searchTerm.toLowerCase())
    ), [data, searchTerm]);

  // Pagination
  const idxLast = currentPage * itemsPerPage;
  const idxFirst = idxLast - itemsPerPage;
  const currentItems = filteredData.slice(idxFirst, idxLast);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, data]);

  const paginate = useCallback((n) => {
    setCurrentPage(n);
    if (device.isMobile) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [device]);

  // Stats
  const totalAmount = useMemo(() =>
    data.reduce((sum, i) => sum + (parseFloat(i.utanga_angahe) || 0), 0), [data]);
  const payingCount = useMemo(() =>
    data.filter(i => i.utanga_umutekano === 'Yego').length, [data]);
  const notPayingCount = useMemo(() =>
    data.filter(i => i.utanga_umutekano === 'Oya').length, [data]);

  const toggleRowExpand = useCallback((id) =>
    setExpandedRow(prev => prev === id ? null : id), []);

  // Pagination range
  const getPaginationRange = useCallback(() => {
    const maxVis = device.isFoldOuter ? 3 : device.isMobile ? 3 : device.isTablet ? 5 : 7;
    let start = Math.max(1, currentPage - Math.floor(maxVis / 2));
    let end = Math.min(totalPages, start + maxVis - 1);
    if (end - start + 1 < maxVis) start = Math.max(1, end - maxVis + 1);
    const r = [];
    for (let i = start; i <= end; i++) r.push(i);
    return r;
  }, [device, currentPage, totalPages]);

  // ════════════════════════════════════════════════════
  // STAT CARDS CONFIG
  // ════════════════════════════════════════════════════
  const statCards = useMemo(() => [
    {
      title: device.isMobile ? "Bose" : "Abaturage Bose",
      val: data.length,
      gradient: theme.gradient1,
      glow: theme.primaryGlow,
      icon: "people-fill",
      iconColor: theme.primary,
      percent: 100
    },
    {
      title: device.isMobile ? "Batanze" : "Abatanze Umutekano",
      val: payingCount,
      gradient: theme.gradient2,
      glow: theme.successGlow,
      icon: "check-circle-fill",
      iconColor: theme.success,
      percent: data.length ? Math.round((payingCount / data.length) * 100) : 0
    },
    {
      title: device.isMobile ? "Bataratanga" : "Abataratanga",
      val: notPayingCount,
      gradient: theme.gradient3,
      glow: theme.dangerGlow,
      icon: "x-circle-fill",
      iconColor: theme.danger,
      percent: data.length ? Math.round((notPayingCount / data.length) * 100) : 0
    },
    {
      title: device.isMobile ? "Amafaranga" : "Amafaranga Yose",
      val: device.isFoldOuter ? `${(totalAmount / 1000).toFixed(0)}K` : `${totalAmount.toLocaleString()} RWF`,
      gradient: theme.gradient4,
      glow: theme.infoGlow,
      icon: "cash-stack",
      iconColor: theme.info,
      percent: 100
    },
  ], [data.length, payingCount, notPayingCount, totalAmount, device, theme]);

  // ════════════════════════════════════════════════════
  // MOBILE CARD COMPONENT
  // ════════════════════════════════════════════════════
  const MobileCard = ({ item, index }) => {
    const isExpanded = expandedRow === item.id;
    const isYego = item.utanga_umutekano === 'Yego';
    const pad = device.isFoldOuter ? '0.5rem' : device.isXSmall ? '0.6rem' : '0.8rem';

    return (
      <div
        style={{
          background: theme.card,
          border: `1px solid ${isExpanded ? theme.primary : theme.border}`,
          borderRadius: rs.cardRadius,
          marginBottom: device.isFoldOuter ? '0.5rem' : '0.65rem',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          boxShadow: isExpanded ? `0 8px 30px ${theme.primaryGlow}` : 'none',
        }}
      >
        {/* Header - Always visible */}
        <div
          onClick={() => toggleRowExpand(item.id)}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleRowExpand(item.id); } }}
          style={{
            padding: pad,
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '0.4rem',
            minHeight: 48,
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.15rem' }}>
              <span style={{
                background: theme.primary,
                color: '#ffffff',
                borderRadius: '50%',
                width: device.isFoldOuter ? 20 : device.isXSmall ? 22 : 26,
                height: device.isFoldOuter ? 20 : device.isXSmall ? 22 : 26,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: device.isFoldOuter ? '0.5rem' : '0.6rem',
                fontWeight: 700,
                flexShrink: 0,
              }}>
                {idxFirst + index + 1}
              </span>
              <h6 style={{
                color: theme.textBright,
                margin: 0,
                fontSize: device.isFoldOuter ? '0.75rem' : device.isXSmall ? '0.82rem' : '0.9rem',
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {item.amazina_y_umuturage}
              </h6>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.2rem',
                padding: device.isFoldOuter ? '0.1rem 0.3rem' : '0.15rem 0.45rem',
                borderRadius: '12px',
                fontSize: device.isFoldOuter ? '0.5rem' : '0.6rem',
                fontWeight: 600,
                color: isYego ? theme.success : theme.danger,
                background: isYego ? 'rgba(0, 214, 143, 0.1)' : 'rgba(255, 61, 113, 0.1)',
                border: `1px solid ${isYego ? 'rgba(0, 214, 143, 0.2)' : 'rgba(255, 61, 113, 0.2)'}`,
              }}>
                <i className={`bi bi-${isYego ? 'check-circle' : 'x-circle'}`}
                  style={{ fontSize: device.isFoldOuter ? '0.45rem' : '0.55rem' }} />
                {item.utanga_umutekano}
              </span>
              <span style={{
                color: item.utanga_umutekano === 'Oya' ? theme.danger : theme.success,
                fontWeight: 700,
                fontSize: device.isFoldOuter ? '0.6rem' : '0.7rem',
              }}>
                {item.utanga_umutekano === 'Oya'
                  ? '0 RWF'
                  : `${(parseFloat(item.utanga_angahe) || 0).toLocaleString()} RWF`}
              </span>
            </div>
          </div>

          <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}
            style={{ color: theme.muted, fontSize: '0.8rem', flexShrink: 0 }} />
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div style={{ borderTop: `1px solid ${theme.border}`, animation: 'fadeIn 0.25s ease' }}>
            {/* All Data Fields */}
            <div style={{
              padding: pad,
              display: 'grid',
              gridTemplateColumns: device.isFoldOuter ? '1fr' : '1fr 1fr',
              gap: device.isFoldOuter ? '0.4rem' : '0.6rem',
            }}>
              <InfoCell label="Amazina" value={item.amazina_y_umuturage} device={device} theme={theme} />
              <InfoCell label="Utanga Umutekano" value={item.utanga_umutekano}
                badge badgeColor={isYego ? theme.success : theme.danger}
                badgeBg={isYego ? 'rgba(0, 214, 143, 0.12)' : 'rgba(255, 61, 113, 0.12)'}
                device={device} theme={theme} />
              <InfoCell label="Amafaranga"
                value={item.utanga_umutekano === 'Oya'
                  ? '0 RWF'
                  : `${(parseFloat(item.utanga_angahe) || 0).toLocaleString()} RWF`}
                valueColor={item.utanga_umutekano === 'Oya' ? theme.danger : theme.success}
                device={device} theme={theme} />
              <InfoCell label="Itariki"
                value={item.created_at ? new Date(item.created_at).toLocaleDateString('rw-RW') : '—'}
                device={device} theme={theme} />
            </div>

            {/* Action Buttons */}
            <div style={{
              borderTop: `1px solid ${theme.border}`,
              padding: pad,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: device.isFoldOuter ? '0.3rem' : '0.4rem',
            }}>
              <MobileActionBtn
                label={device.isFoldOuter ? '' : 'Hindura'}
                icon="pencil"
                color="#818cf8"
                bg="rgba(11, 61, 145, 0.08)"
                border="rgba(11, 61, 145, 0.25)"
                onClick={() => handleEdit(item)}
                device={device}
              />
              <MobileActionBtn
                label={device.isFoldOuter ? '' : 'Siba'}
                icon="trash"
                color="#dc2626"
                bg="rgba(255, 61, 113, 0.08)"
                border="rgba(255, 61, 113, 0.25)"
                onClick={() => handleDeleteClick(item)}
                device={device}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  // ════════════════════════════════════════════════════
  // SUB-COMPONENTS
  // ════════════════════════════════════════════════════
  const InfoCell = ({ label, value, badge, badgeColor, badgeBg, valueColor, device: d, theme: t }) => (
    <div>
      <div style={{
        color: '#64748b',
        fontSize: d.isFoldOuter ? '0.48rem' : d.isXSmall ? '0.52rem' : '0.58rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        fontWeight: 600,
        marginBottom: '2px',
      }}>{label}</div>
      {badge ? (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.2rem',
          padding: '0.15rem 0.4rem',
          borderRadius: '10px',
          fontSize: d.isFoldOuter ? '0.55rem' : '0.65rem',
          fontWeight: 600,
          color: badgeColor,
          background: badgeBg,
        }}>
          <i className={`bi bi-${value === 'Yego' ? 'check-circle' : 'x-circle'}`}
            style={{ fontSize: '0.55rem' }} />
          {value}
        </span>
      ) : (
        <div style={{
          color: valueColor || t.text,
          fontSize: d.isFoldOuter ? '0.7rem' : d.isXSmall ? '0.75rem' : '0.82rem',
          fontWeight: 500,
          wordBreak: 'break-word',
        }}>
          {value || '—'}
        </div>
      )}
    </div>
  );

  const MobileActionBtn = ({ label, icon, color, bg, border, onClick, device: d }) => (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{
        background: bg,
        border: `1px solid ${border}`,
        color: color,
        borderRadius: d.isFoldOuter ? '8px' : '10px',
        padding: d.isFoldOuter ? '0.45rem' : '0.55rem',
        cursor: 'pointer',
        fontSize: d.isFoldOuter ? '0.6rem' : '0.7rem',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.25rem',
        minHeight: 44,
        fontFamily: 'inherit',
        transition: 'all 0.2s ease',
      }}
    >
      <i className={`bi bi-${icon}`} />
      {label && <span>{label}</span>}
    </button>
  );

  // ════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════
  return (
    <div
      style={{
        background: theme.bg,
        color: theme.text,
        minHeight: '100vh',
        minHeight: '100dvh',
        padding: rs.containerPad,
        paddingBottom: device.isMobile ? '5rem' : '1.5rem',
        fontFamily: "'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        WebkitOverflowScrolling: 'touch',
        
        overflowX: 'hidden',
      }}
    >
      {/* ════════════════════════════════════════════
          GLOBAL RESPONSIVE STYLES
      ════════════════════════════════════════════ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800&display=swap');

        *{
          box-sizing:border-box;
          -webkit-tap-highlight-color:transparent;
          font-family:'Nunito Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        }
        html{
          -webkit-text-size-adjust:100%;
          -ms-text-size-adjust:100%;
          scroll-behavior:smooth;
        }
        body{
          -webkit-font-smoothing:antialiased;
          -moz-osx-font-smoothing:grayscale;
          overscroll-behavior:none;
        }

        @keyframes fadeIn{
          from{opacity:0;transform:translateY(-6px)}
          to{opacity:1;transform:translateY(0)}
        }
        @keyframes slideUp{
          from{opacity:0;transform:translateY(16px)}
          to{opacity:1;transform:translateY(0)}
        }
        @keyframes cardIn{
          from{opacity:0;transform:translateY(22px) scale(0.97)}
          to{opacity:1;transform:translateY(0) scale(1)}
        }
        @keyframes countUp{
          from{opacity:0;transform:translateY(8px)}
          to{opacity:1;transform:translateY(0)}
        }
        @keyframes shimmer{
          0%{background-position:-200% 0}
          100%{background-position:200% 0}
        }
        @keyframes pulse-danger{
          0%{box-shadow:0 0 0 0 rgba(255,61,113,0.4)}
          70%{box-shadow:0 0 0 12px rgba(255,61,113,0)}
          100%{box-shadow:0 0 0 0 rgba(255,61,113,0)}
        }

        /* Safe Areas */
        @supports(padding:env(safe-area-inset-top)){
          .safe-area{
            padding-top:env(safe-area-inset-top);
            padding-bottom:env(safe-area-inset-bottom);
            padding-left:env(safe-area-inset-left);
            padding-right:env(safe-area-inset-right);
          }
        }

        /* Stat Cards */
        .umutekano-stat-card{
          transition:all 0.35s cubic-bezier(0.4,0,0.2,1)!important;
          -webkit-transform:translateZ(0);transform:translateZ(0);
          animation:cardIn 0.5s ease both;
        }
        .umutekano-stat-card:nth-child(1){animation-delay:0.05s}
        .umutekano-stat-card:nth-child(2){animation-delay:0.12s}
        .umutekano-stat-card:nth-child(3){animation-delay:0.19s}
        .umutekano-stat-card:nth-child(4){animation-delay:0.26s}
        .umutekano-stat-card:active{transform:scale(0.97)!important}
        @media(hover:hover){
          .umutekano-stat-card:hover{
            transform:translateY(-7px) scale(1.02)!important;
            box-shadow:0 24px 60px rgba(0,0,0,0.45)!important;
          }
          .umutekano-stat-card:hover .card-icon-box{transform:scale(1.1) rotate(-4deg)!important}
          .hdr-add-btn:hover{transform:translateY(-2px)!important;filter:brightness(1.15)!important}
          .hdr-refresh-btn:hover{border-color:rgba(255,255,255,0.4)!important;color:#ffffff!important;background:rgba(255,255,255,0.16)!important}
        }
        .card-icon-box{transition:transform 0.3s ease!important}
        .hdr-add-btn{transition:all 0.25s ease!important}
        .hdr-refresh-btn{transition:all 0.25s ease!important}

        /* Table */
        .table-row-hover{transition:all 0.2s ease}
        @media(hover:hover){.table-row-hover:hover td{background:rgba(11,61,145,0.06)!important}}
        .table-row-hover:active td{background:rgba(11,61,145,0.1)!important}

        /* Form Controls */
        .form-control,.form-select{
          font-size:16px!important;
        }
        @media(min-width:576px){
          .form-control,.form-select{font-size:0.85rem!important}
        }
        .form-control:focus,.form-select:focus{
          box-shadow:0 0 0 3px ${theme.primaryGlow}!important;
          border-color:${theme.primary}!important;
        }
        .form-control::placeholder{color:${theme.muted};opacity:0.6}
        .form-select option{background:${theme.card};color:${theme.text}}

        /* Pagination */
        .pagination .page-link{
          background:${theme.bgSecondary}!important;
          border-color:${theme.border}!important;
          color:${theme.muted}!important;
          transition:all 0.2s ease!important;
          display:flex;align-items:center;justify-content:center;
          padding:0!important;
        }
        .pagination .page-link:active{background:${theme.card}!important}
        @media(hover:hover){
          .pagination .page-link:hover{background:${theme.card}!important;border-color:${theme.primary}!important;color:#0d1b2a!important}
        }
        .pagination .page-item.active .page-link{
          background:${theme.gradient1}!important;
          border-color:${theme.primary}!important;
          color:#ffffff!important;
        }
        .pagination .page-item.disabled .page-link{opacity:0.35}

        /* Pagination sizes */
        @media(max-width:359px){.pagination .page-link{min-width:28px;height:28px;font-size:0.6rem;margin:0 1px!important;border-radius:5px!important}}
        @media(min-width:360px) and (max-width:575px){.pagination .page-link{min-width:32px;height:32px;font-size:0.7rem;margin:0 1px!important;border-radius:6px!important}}
        @media(min-width:576px) and (max-width:991px){.pagination .page-link{min-width:36px;height:36px;font-size:0.75rem;margin:0 2px!important;border-radius:8px!important}}
        @media(min-width:992px){.pagination .page-link{min-width:40px;height:40px;font-size:0.8rem;margin:0 2px!important;border-radius:10px!important}}
        @media(hover:none) and (pointer:coarse){.pagination .page-link{min-width:40px;height:40px}}

        /* Modal */
        .modal-content{background:${theme.card}!important;border:1px solid ${theme.border}!important;color:${theme.text}!important}
        @media(max-width:575px){
          .modal-content{border-radius:20px 20px 0 0!important;margin:0!important}
          .modal-dialog{margin:0!important;max-width:100%!important;display:flex!important;align-items:flex-end!important}
          .modal-dialog-centered{min-height:100%!important}
        }
        @media(min-width:576px){.modal-content{border-radius:20px!important}}

        /* Alert */
        .alert{border:none!important}

        /* Tooltip */
        @media(hover:none){.tooltip{display:none!important}}
        .tooltip-inner{background:${theme.card}!important;border:1px solid ${theme.border}!important;border-radius:8px!important;font-size:0.75rem!important}

        /* FAB */
        .fab-container{position:fixed;z-index:1050}
        @supports(padding:env(safe-area-inset-bottom)){.fab-container{bottom:calc(20px + env(safe-area-inset-bottom))!important}}
        .fab-button{
          width:52px;height:52px;border-radius:50%;
          background:${theme.gradient1};
          border:none;color:white;font-size:1.35rem;
          display:flex;align-items:center;justify-content:center;
          box-shadow:0 8px 25px ${theme.primaryGlow};
          cursor:pointer;transition:all 0.3s ease;
        }
        .fab-button:active{transform:scale(0.88)}
        .fab-menu{
          position:absolute;bottom:62px;right:0;
          background:${theme.card};border:1px solid ${theme.border};
          border-radius:14px;padding:0.4rem;
          min-width:160px;animation:slideUp 0.2s ease;
          box-shadow:0 20px 40px rgba(0,0,0,0.5);
        }
        .fab-menu-item{
          display:flex;align-items:center;gap:0.6rem;
          padding:0.65rem 0.75rem;border-radius:8px;
          color:${theme.muted};background:none;border:none;
          width:100%;font-size:0.8rem;font-weight:500;
          cursor:pointer;transition:all 0.2s ease;
          font-family:inherit;min-height:44px;
        }
        .fab-menu-item:active{background:rgba(11,61,145,0.12);color:#0d1b2a}
        @media(hover:hover){.fab-menu-item:hover{background:rgba(11,61,145,0.08);color:#0d1b2a}}

        /* Scrollbar */
        ::-webkit-scrollbar{width:5px;height:5px}
        ::-webkit-scrollbar-track{background:${theme.bg}}
        ::-webkit-scrollbar-thumb{background:${theme.border};border-radius:3px}
        *{scrollbar-width:thin;scrollbar-color:${theme.border} ${theme.bg}}

        /* Landscape */
        @media(max-height:480px) and (orientation:landscape){
          .modal-body{max-height:55vh!important;overflow-y:auto!important}
        }

        /* Reduced motion */
        @media(prefers-reduced-motion:reduce){*{transition:none!important;animation:none!important}}

        /* High contrast */
        @media(prefers-contrast:high){
          .umutekano-stat-card{border-width:2px!important}
          .badge{font-weight:700!important}
        }

        /* Dark mode inputs */
        @media(prefers-color-scheme:dark){input,select,textarea{color-scheme:dark}}

        /* Print */
        @media print{
          body{background:#0d1b2a!important;color:#ffffff!important}
          .fab-container,.modal{display:none!important}
          .umutekano-stat-card{border:1px solid #ccc!important;background:#0d1b2a!important}
          .umutekano-stat-card *{color:#ffffff!important}
        }
      `}</style>

      <div className="safe-area">
        {/* ════════════════════════════════════════════
            HEADER
        ════════════════════════════════════════════ */}
        <div style={{
          background: 'linear-gradient(135deg, #0B3D91 0%, #093278 60%, #0B3D91 100%)',
          border: `1px solid ${theme.borderLight}`,
          borderRadius: device.isMobile ? '14px' : '20px',
          padding: device.isFoldOuter ? '0.7rem' : device.isMobile ? '0.9rem 1rem' : '1.4rem 1.8rem',
          marginBottom: device.isMobile ? '0.75rem' : '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.75rem',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
        }}>
          {/* Background glow orb */}
          <div style={{
            position: 'absolute', top: '-40px', right: '-40px',
            width: 180, height: 180, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(11,61,145,0.18) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', bottom: '-30px', left: '30%',
            width: 120, height: 120, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0,149,255,0.1) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          {/* Left: icon + title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: device.isMobile ? '0.65rem' : '1rem', minWidth: 0, zIndex: 1 }}>
            <div style={{
              width: device.isFoldOuter ? 36 : device.isMobile ? 42 : 52,
              height: device.isFoldOuter ? 36 : device.isMobile ? 42 : 52,
              borderRadius: device.isMobile ? '12px' : '16px',
              background: 'linear-gradient(135deg, #0B3D91 0%, #093278 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 6px 20px ${theme.primaryGlow}`,
              flexShrink: 0,
            }}>
              <i className="bi bi-shield-lock-fill" style={{
                color: '#ffffff',
                fontSize: device.isFoldOuter ? '0.9rem' : device.isMobile ? '1.1rem' : '1.4rem',
              }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h2 style={{
                color: '#ffffff', fontWeight: 800, margin: 0,
                fontSize: rs.headerTitle, lineHeight: 1.2,
                letterSpacing: '-0.02em',
              }}>
                {device.isFoldOuter ? 'Umutekano' : device.isMobile ? 'Imisanzu' : "Imisanzu y'Umutekano"}
              </h2>
              {!device.isFoldOuter && (
                <p style={{
                  color: 'rgba(255,255,255,0.75)', margin: '2px 0 0',
                  fontSize: rs.headerSub, lineHeight: 1.4,
                }}>
                  {device.isMobile
                    ? "Gucunga imisanzu y'umutekano"
                    : "Gucunga no gukurikirana imisanzu y'umutekano w'abaturage"}
                </p>
              )}
            </div>
          </div>

          {/* Right: action buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', zIndex: 1, flexShrink: 0 }}>
            <button
              className="hdr-add-btn"
              onClick={() => { resetForm(); setShowForm(true); }}
              style={{
                background: 'linear-gradient(135deg, #0B3D91 0%, #093278 100%)',
                border: 'none',
                borderRadius: '50px',
                padding: device.isFoldOuter ? '0.4rem 0.7rem' : device.isMobile ? '0.5rem 0.9rem' : '0.6rem 1.4rem',
                fontWeight: 700,
                color: '#ffffff',
                boxShadow: `0 4px 20px ${theme.primaryGlow}`,
                cursor: 'pointer',
                fontSize: device.isFoldOuter ? '0.6rem' : device.isMobile ? '0.75rem' : '0.85rem',
                fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: '0.35rem',
                whiteSpace: 'nowrap',
              }}
            >
              <i className="bi bi-plus-lg" />
              {!device.isFoldOuter && 'Ongeraho'}
            </button>
            <button
              className="hdr-refresh-btn"
              onClick={fetchRecords}
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: `1px solid ${theme.borderLight}`,
                borderRadius: '50px',
                color: 'rgba(255,255,255,0.85)',
                padding: device.isFoldOuter ? '0.4rem 0.6rem' : device.isMobile ? '0.5rem 0.75rem' : '0.6rem 1rem',
                cursor: 'pointer',
                fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: '0.3rem',
                fontSize: device.isMobile ? '0.75rem' : '0.85rem',
              }}
            >
              <i className="bi bi-arrow-clockwise" />
              {!device.isMobile && <span style={{ fontSize: '0.82rem' }}>Vugurura</span>}
            </button>
          </div>
        </div>

        {/* ════════════════════════════════════════════
            ALERT
        ════════════════════════════════════════════ */}
        {message.text && (
          <Alert
            variant={message.variant}
            dismissible
            onClose={() => setMessage({ text: '', variant: '' })}
            className="mb-3"
            style={{
              borderRadius: device.isMobile ? '10px' : '14px',
              fontSize: device.isMobile ? '0.78rem' : '0.85rem',
            }}
          >
            {message.text}
          </Alert>
        )}

        {/* ════════════════════════════════════════════
            STAT CARDS
        ════════════════════════════════════════════ */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: device.isMobile ? '1fr 1fr' : device.isTablet ? '1fr 1fr' : 'repeat(4,1fr)',
          gap: device.isFoldOuter ? '0.35rem' : device.isMobile ? '0.5rem' : '1rem',
          marginBottom: device.isMobile ? '0.75rem' : '1.5rem',
        }}>
          {statCards.map((s, i) => (
            <div
              key={i}
              className="umutekano-stat-card"
              style={{
                background: `linear-gradient(145deg, ${theme.card} 0%, ${theme.bgSecondary} 100%)`,
                borderRadius: rs.cardRadius,
                padding: rs.cardPad,
                border: `1px solid ${theme.border}`,
                position: 'relative',
                overflow: 'hidden',
                boxShadow: `0 8px 28px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04)`,
                cursor: 'default',
              }}
            >
              {/* Gradient glow blob */}
              <div style={{
                position: 'absolute', top: '-30px', right: '-30px',
                width: device.isMobile ? 90 : 130,
                height: device.isMobile ? 90 : 130,
                borderRadius: '50%',
                background: s.gradient,
                opacity: 0.12,
                filter: 'blur(28px)',
                pointerEvents: 'none',
              }} />
              {/* Bottom shimmer line */}
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                height: '2px',
                background: s.gradient,
                opacity: 0.6,
              }} />

              {/* Top row: icon + badge */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                marginBottom: device.isMobile ? '0.5rem' : '0.85rem',
              }}>
                <div
                  className="card-icon-box"
                  style={{
                    width: rs.iconSize,
                    height: rs.iconSize,
                    borderRadius: device.isMobile ? '10px' : '14px',
                    background: s.gradient,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 6px 20px ${s.glow}`,
                    flexShrink: 0,
                  }}
                >
                  <i className={`bi bi-${s.icon}`} style={{
                    fontSize: device.isFoldOuter ? '0.85rem' : device.isMobile ? '1rem' : '1.3rem',
                    color: '#ffffff',
                  }} />
                </div>
                {/* Percent badge */}
                <div style={{
                  background: `${s.glow}`,
                  border: `1px solid ${s.iconColor}30`,
                  borderRadius: '20px',
                  padding: device.isFoldOuter ? '0.12rem 0.3rem' : '0.22rem 0.6rem',
                  fontSize: device.isFoldOuter ? '0.48rem' : device.isMobile ? '0.58rem' : '0.7rem',
                  fontWeight: 700,
                  color: s.iconColor,
                  letterSpacing: '0.02em',
                  backdropFilter: 'blur(8px)',
                }}>
                  {s.percent}%
                </div>
              </div>

              {/* Value */}
              <div style={{
                fontSize: rs.statValue,
                fontWeight: 800,
                color: '#0d1b2a',
                lineHeight: 1.1,
                letterSpacing: '-0.03em',
                marginBottom: '0.2rem',
                animation: 'countUp 0.5s ease both',
              }}>
                {s.val}
              </div>

              {/* Label */}
              <div style={{
                color: '#64748b',
                fontSize: rs.statTitle,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: device.isFoldOuter ? '0' : '0.08em',
                marginBottom: device.isMobile ? '0.45rem' : '0.75rem',
              }}>
                {s.title}
              </div>

              {/* Progress bar */}
              <div style={{
                height: device.isMobile ? '3px' : '4px',
                borderRadius: '4px',
                background: 'rgba(15,23,42,0.06)',
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  width: `${s.percent}%`,
                  borderRadius: '4px',
                  background: s.gradient,
                  boxShadow: `0 0 8px ${s.glow}`,
                  transition: 'width 1.2s cubic-bezier(0.4,0,0.2,1)',
                }} />
              </div>
            </div>
          ))}
        </div>

        {/* ════════════════════════════════════════════
            FORM (Inline for Desktop, Full-screen modal for Mobile)
        ════════════════════════════════════════════ */}
        {showForm && !device.isMobile && (
          <div style={{
            background: theme.card,
            borderRadius: rs.cardRadius,
            border: `1px solid ${theme.border}`,
            marginBottom: '1.5rem',
            overflow: 'hidden',
          }}>
            <div style={{
              background: `linear-gradient(135deg, ${theme.primary} 0%, #093278 100%)`,
              color: 'white',
              padding: device.isTablet ? '0.9rem 1.2rem' : '1.1rem 1.5rem',
            }}>
              <h5 className="mb-0 fw-bold" style={{ fontSize: device.isTablet ? '0.9rem' : '1rem' }}>
                <i className={`bi ${editingId ? 'bi-pencil-square' : 'bi-plus-circle'} me-2`} />
                {editingId ? 'Hindura Amakuru' : 'Ongeraho Umuturage Mushya'}
              </h5>
            </div>
            <div style={{ padding: device.isTablet ? '1.2rem' : '1.5rem' }}>
              <Form onSubmit={handleSubmit}>
                <Row className="g-3">
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label style={{ color: theme.text, fontWeight: 600, fontSize: rs.formLabel }}>
                        Amazina y'Umuturage *
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Amazina yuzuye"
                        value={formData.amazina_y_umuturage}
                        onChange={(e) => setFormData({ ...formData, amazina_y_umuturage: e.target.value })}
                        required
                        style={{
                          background: theme.bgSecondary,
                          color: theme.text,
                          borderColor: theme.border,
                          borderRadius: '10px',
                          padding: rs.formPad,
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label style={{ color: theme.text, fontWeight: 600, fontSize: rs.formLabel }}>
                        Utanga? *
                      </Form.Label>
                      <Form.Select
                        value={formData.utanga_umutekano}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            utanga_umutekano: val,
                            utanga_angahe: val === 'Oya' ? 0 : prev.utanga_angahe,
                          }));
                        }}
                        required
                        style={{
                          background: theme.bgSecondary,
                          color: theme.text,
                          borderColor: theme.border,
                          borderRadius: '10px',
                          padding: rs.formPad,
                        }}
                      >
                        <option value="Yego">Yego</option>
                        <option value="Oya">Oya</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label style={{ color: theme.text, fontWeight: 600, fontSize: rs.formLabel }}>
                        Amafaranga (RWF)
                        {formData.utanga_umutekano === 'Oya' && (
                          <span style={{ color: theme.danger, fontSize: '0.72rem', marginLeft: '0.4rem', fontWeight: 500 }}>
                            (Ntatanga — 0 RWF)
                          </span>
                        )}
                      </Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="0"
                        value={formData.utanga_angahe}
                        onChange={(e) => setFormData(prev => ({ ...prev, utanga_angahe: e.target.value }))}
                        min="0"
                        inputMode="numeric"
                        disabled={formData.utanga_umutekano === 'Oya'}
                        style={{
                          background: formData.utanga_umutekano === 'Oya'
                            ? 'rgba(255,61,113,0.06)'
                            : theme.bgSecondary,
                          color: formData.utanga_umutekano === 'Oya' ? theme.danger : theme.text,
                          borderColor: formData.utanga_umutekano === 'Oya'
                            ? 'rgba(255,61,113,0.3)'
                            : theme.border,
                          borderRadius: '10px',
                          padding: rs.formPad,
                          cursor: formData.utanga_umutekano === 'Oya' ? 'not-allowed' : 'text',
                          opacity: formData.utanga_umutekano === 'Oya' ? 0.6 : 1,
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} className="d-flex justify-content-end gap-2 mt-2">
                    <button type="button" onClick={resetForm} style={{
                      background: 'transparent',
                      border: `1px solid ${theme.borderLight}`,
                      color: theme.muted,
                      borderRadius: '10px',
                      padding: '0.5rem 1.2rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: '0.85rem',
                    }}>
                      <i className="bi bi-x-circle me-1" />Hagarika
                    </button>
                    <button type="submit" style={{
                      background: theme.success,
                      border: 'none',
                      color: '#ffffff',
                      borderRadius: '10px',
                      padding: '0.5rem 1.4rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: `0 4px 15px ${theme.successGlow}`,
                      fontFamily: 'inherit',
                      fontSize: '0.85rem',
                    }}>
                      <i className="bi bi-check-circle me-1" />
                      {editingId ? 'Vugurura' : 'Bika'}
                    </button>
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        )}

        {/* Mobile Form Modal */}
        <Modal
          show={showForm && device.isMobile}
          onHide={resetForm}
          fullscreen
          scrollable
        >
          <Modal.Header
            closeButton
            closeVariant="white"
            style={{
              background: `linear-gradient(135deg, ${theme.primary} 0%, #093278 100%)`,
              border: 'none',
              padding: '1rem',
            }}
          >
            <Modal.Title style={{ fontSize: '0.95rem', color: '#ffffff' }}>
              <i className={`bi ${editingId ? 'bi-pencil-square' : 'bi-plus-circle'} me-2`} />
              {editingId ? 'Hindura' : 'Ongeraho Mushya'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ background: theme.bg, padding: '1rem' }}>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label style={{ color: theme.text, fontWeight: 600, fontSize: '0.82rem' }}>
                  Amazina y'Umuturage *
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Amazina yuzuye"
                  value={formData.amazina_y_umuturage}
                  onChange={(e) => setFormData({ ...formData, amazina_y_umuturage: e.target.value })}
                  required
                  autoComplete="name"
                  style={{
                    background: theme.bgSecondary,
                    color: theme.text,
                    borderColor: theme.border,
                    borderRadius: '10px',
                    padding: '0.65rem 0.85rem',
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ color: theme.text, fontWeight: 600, fontSize: '0.82rem' }}>
                  Utanga Umutekano? *
                </Form.Label>
                <Form.Select
                  value={formData.utanga_umutekano}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      utanga_umutekano: val,
                      utanga_angahe: val === 'Oya' ? 0 : prev.utanga_angahe,
                    }));
                  }}
                  required
                  style={{
                    background: theme.bgSecondary,
                    color: theme.text,
                    borderColor: theme.border,
                    borderRadius: '10px',
                    padding: '0.65rem 0.85rem',
                  }}
                >
                  <option value="Yego">Yego (Atanga)</option>
                  <option value="Oya">Oya (Ntatanga)</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={{ color: theme.text, fontWeight: 600, fontSize: '0.82rem' }}>
                  Amafaranga (RWF)
                  {formData.utanga_umutekano === 'Oya' && (
                    <span style={{ color: theme.danger, fontSize: '0.7rem', marginLeft: '0.4rem', fontWeight: 500 }}>
                      (Ntatanga — 0 RWF)
                    </span>
                  )}
                </Form.Label>
                <Form.Control
                  type="number"
                  placeholder="0"
                  value={formData.utanga_angahe}
                  onChange={(e) => setFormData(prev => ({ ...prev, utanga_angahe: e.target.value }))}
                  min="0"
                  inputMode="numeric"
                  disabled={formData.utanga_umutekano === 'Oya'}
                  style={{
                    background: formData.utanga_umutekano === 'Oya'
                      ? 'rgba(255,61,113,0.06)'
                      : theme.bgSecondary,
                    color: formData.utanga_umutekano === 'Oya' ? theme.danger : theme.text,
                    borderColor: formData.utanga_umutekano === 'Oya'
                      ? 'rgba(255,61,113,0.3)'
                      : theme.border,
                    borderRadius: '10px',
                    padding: '0.65rem 0.85rem',
                    cursor: formData.utanga_umutekano === 'Oya' ? 'not-allowed' : 'text',
                    opacity: formData.utanga_umutekano === 'Oya' ? 0.6 : 1,
                  }}
                />
              </Form.Group>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <button type="submit" style={{
                  background: theme.success,
                  border: 'none',
                  color: '#ffffff',
                  borderRadius: '10px',
                  padding: '0.75rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: `0 4px 15px ${theme.successGlow}`,
                  fontFamily: 'inherit',
                  fontSize: '0.88rem',
                  width: '100%',
                }}>
                  <i className="bi bi-check-circle me-1" />
                  {editingId ? 'Vugurura' : 'Bika Amakuru'}
                </button>
                <button type="button" onClick={resetForm} style={{
                  background: 'transparent',
                  border: `1px solid ${theme.borderLight}`,
                  color: theme.muted,
                  borderRadius: '10px',
                  padding: '0.65rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: '0.85rem',
                  width: '100%',
                }}>
                  <i className="bi bi-x-circle me-1" />Hagarika
                </button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* ════════════════════════════════════════════
            SEARCH / FILTER
        ════════════════════════════════════════════ */}
        <div style={{
          background: theme.card,
          borderRadius: device.isMobile ? '12px' : '16px',
          padding: device.isFoldOuter ? '0.4rem' : device.isMobile ? '0.5rem' : '0.75rem 1rem',
          border: `1px solid ${theme.border}`,
          marginBottom: device.isMobile ? '0.6rem' : '1rem',
        }}>
          <InputGroup>
            <InputGroup.Text style={{
              background: theme.bgSecondary,
              borderColor: theme.border,
              color: theme.muted,
              borderRadius: `${device.isMobile ? '8px' : '10px'} 0 0 ${device.isMobile ? '8px' : '10px'}`,
              padding: device.isFoldOuter ? '0.35rem 0.5rem' : undefined,
            }}>
              <i className="bi bi-search" />
            </InputGroup.Text>
            <Form.Control
              placeholder={device.isFoldOuter ? 'Shakisha...' : device.isMobile ? 'Shakisha amazina...' : 'Shakisha amazina y\'umuturage...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                background: theme.bgSecondary,
                color: theme.text,
                borderColor: theme.border,
                borderRadius: `0 ${device.isMobile ? '8px' : '10px'} ${device.isMobile ? '8px' : '10px'} 0`,
                padding: device.isFoldOuter ? '0.35rem 0.5rem' : undefined,
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  zIndex: 5, background: 'none', border: 'none', color: theme.muted,
                  cursor: 'pointer', padding: '4px',
                }}
              >
                <i className="bi bi-x-lg" style={{ fontSize: '0.7rem' }} />
              </button>
            )}
          </InputGroup>
          {searchTerm && (
            <div style={{ marginTop: '0.3rem' }}>
              <small style={{ color: theme.muted, fontSize: device.isMobile ? '0.6rem' : '0.72rem' }}>
                <i className="bi bi-info-circle me-1" />
                Byasanze: <strong style={{ color: theme.text }}>{filteredData.length}</strong> / {data.length}
              </small>
            </div>
          )}
        </div>

        {/* ════════════════════════════════════════════
            DATA CONTENT
        ════════════════════════════════════════════ */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{
              color: theme.primary,
              width: device.isMobile ? '2rem' : '3rem',
              height: device.isMobile ? '2rem' : '3rem',
            }} />
            <div className="mt-3" style={{ color: theme.muted, fontSize: device.isMobile ? '0.8rem' : '0.9rem' }}>
              Ibyanditswe biragarurwa...
            </div>
          </div>
        ) : currentItems.length === 0 ? (
          <div className="text-center py-5" style={{
            background: theme.card,
            borderRadius: rs.cardRadius,
            border: `1px solid ${theme.border}`,
          }}>
            <i className="bi bi-people" style={{ fontSize: device.isMobile ? '3rem' : '4.5rem', color: theme.border }} />
            <h4 className="mt-3" style={{ color: theme.muted, fontSize: device.isMobile ? '0.95rem' : '1.15rem' }}>
              Nta muturage wahiswe
            </h4>
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              style={{
                background: theme.primary,
                border: 'none',
                borderRadius: '10px',
                padding: '0.55rem 1.2rem',
                fontWeight: 600,
                color: '#ffffff',
                cursor: 'pointer',
                marginTop: '0.75rem',
                fontFamily: 'inherit',
                fontSize: '0.85rem',
              }}
            >
              <i className="bi bi-plus-circle me-2" />Ongeraho
            </button>
          </div>
        ) : (
          <>
            {/* ═══ MOBILE: Card Layout ═══ */}
            {device.isMobile ? (
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.35rem',
                  padding: '0 0.1rem',
                }}>
                  <small style={{ color: theme.muted, fontSize: device.isFoldOuter ? '0.5rem' : '0.62rem' }}>
                    {idxFirst + 1}–{Math.min(idxLast, filteredData.length)} / {filteredData.length}
                  </small>
                  <small style={{ color: '#475569', fontSize: device.isFoldOuter ? '0.45rem' : '0.55rem' }}>
                    Kanda → reba byose
                  </small>
                </div>
                {currentItems.map((item, i) => (
                  <MobileCard key={item.id} item={item} index={i} />
                ))}
              </div>
            ) : (
              /* ═══ DESKTOP/TABLET: Table Layout ═══ */
              <div style={{
                background: theme.card,
                borderRadius: rs.cardRadius,
                border: `1px solid ${theme.border}`,
                overflow: 'hidden',
                boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
              }}>
                <div className="table-responsive" style={{ WebkitOverflowScrolling: 'touch' }}>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead>
                      <tr>
                        {['#', 'Amazina', 'Utanga?', 'Amafaranga', 'Itariki', 'Ibikorwa'].map((h, i) => (
                          <th key={i} style={{
                            background: theme.bgSecondary,
                            color: theme.muted,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            fontSize: device.isTablet ? '0.62rem' : '0.7rem',
                            padding: device.isTablet ? '0.85rem 0.5rem' : '1rem 0.75rem',
                            borderBottom: `2px solid ${theme.borderLight}`,
                            textAlign: [0, 2, 5].includes(i) ? 'center' : 'left',
                            whiteSpace: 'nowrap',
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                          }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((item, index) => {
                        const isYego = item.utanga_umutekano === 'Yego';
                        return (
                          <tr key={item.id} className="table-row-hover">
                            <td style={{
                              padding: device.isTablet ? '0.85rem 0.5rem' : '1rem 0.75rem',
                              borderBottom: `1px solid ${theme.border}`,
                              textAlign: 'center',
                            }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: device.isTablet ? '26px' : '30px',
                                height: device.isTablet ? '26px' : '30px',
                                borderRadius: '8px',
                                background: 'rgba(11, 61, 145, 0.1)',
                                color: theme.primary,
                                fontSize: device.isTablet ? '0.7rem' : '0.78rem',
                                fontWeight: 700,
                              }}>
                                {idxFirst + index + 1}
                              </span>
                            </td>
                            <td style={{
                              padding: device.isTablet ? '0.85rem 0.5rem' : '1rem 0.75rem',
                              borderBottom: `1px solid ${theme.border}`,
                              color: theme.textBright,
                              fontWeight: 600,
                              fontSize: device.isTablet ? '0.8rem' : '0.85rem',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <div style={{
                                  width: device.isTablet ? '30px' : '36px',
                                  height: device.isTablet ? '30px' : '36px',
                                  borderRadius: '10px',
                                  background: `linear-gradient(135deg, ${theme.primary}33, ${theme.primary}11)`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}>
                                  <i className="bi bi-person-fill" style={{
                                    color: theme.primary,
                                    fontSize: device.isTablet ? '0.8rem' : '0.95rem',
                                  }} />
                                </div>
                                <span style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: device.isTablet ? '130px' : 'none',
                                }}>
                                  {item.amazina_y_umuturage}
                                </span>
                              </div>
                            </td>
                            <td style={{
                              padding: device.isTablet ? '0.85rem 0.5rem' : '1rem 0.75rem',
                              borderBottom: `1px solid ${theme.border}`,
                              textAlign: 'center',
                            }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.3rem',
                                padding: device.isTablet ? '0.3rem 0.6rem' : '0.35rem 0.8rem',
                                borderRadius: '16px',
                                fontSize: device.isTablet ? '0.7rem' : '0.78rem',
                                fontWeight: 600,
                                color: isYego ? theme.success : theme.danger,
                                background: isYego ? 'rgba(0,214,143,0.1)' : 'rgba(255,61,113,0.1)',
                                border: `1px solid ${isYego ? 'rgba(0,214,143,0.2)' : 'rgba(255,61,113,0.2)'}`,
                              }}>
                                <i className={`bi bi-${isYego ? 'check-circle' : 'x-circle'}`}
                                  style={{ fontSize: '0.7rem' }} />
                                {item.utanga_umutekano}
                              </span>
                            </td>
                            <td style={{
                              padding: device.isTablet ? '0.85rem 0.5rem' : '1rem 0.75rem',
                              borderBottom: `1px solid ${theme.border}`,
                              fontWeight: 700,
                              color: item.utanga_umutekano === 'Oya' ? theme.danger : theme.success,
                              fontSize: device.isTablet ? '0.8rem' : '0.85rem',
                            }}>
                              {item.utanga_umutekano === 'Oya'
                                ? '0 RWF'
                                : `${(parseFloat(item.utanga_angahe) || 0).toLocaleString()} RWF`}
                            </td>
                            <td style={{
                              padding: device.isTablet ? '0.85rem 0.5rem' : '1rem 0.75rem',
                              borderBottom: `1px solid ${theme.border}`,
                              color: theme.muted,
                              fontSize: device.isTablet ? '0.75rem' : '0.82rem',
                            }}>
                              {item.created_at
                                ? new Date(item.created_at).toLocaleDateString('rw-RW')
                                : '—'}
                            </td>
                            <td style={{
                              padding: device.isTablet ? '0.85rem 0.5rem' : '1rem 0.75rem',
                              borderBottom: `1px solid ${theme.border}`,
                              textAlign: 'center',
                            }}>
                              <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                {[
                                  {
                                    tip: 'Hindura',
                                    icon: 'pencil',
                                    borderColor: 'rgba(11,61,145,0.3)',
                                    bg: 'rgba(11,61,145,0.08)',
                                    color: theme.primary,
                                    action: () => handleEdit(item),
                                  },
                                  {
                                    tip: 'Siba',
                                    icon: 'trash',
                                    borderColor: 'rgba(255,61,113,0.3)',
                                    bg: 'rgba(255,61,113,0.08)',
                                    color: theme.danger,
                                    action: () => handleDeleteClick(item),
                                  },
                                ].map((btn, idx) => (
                                  <OverlayTrigger key={idx} placement="top" overlay={<Tooltip>{btn.tip}</Tooltip>}>
                                    <button
                                      onClick={btn.action}
                                      style={{
                                        width: rs.actionBtn,
                                        height: rs.actionBtn,
                                        borderRadius: device.isTablet ? '8px' : '10px',
                                        border: `1px solid ${btn.borderColor}`,
                                        background: btn.bg,
                                        color: btn.color,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: 0,
                                        fontFamily: 'inherit',
                                      }}
                                    >
                                      <i className={`bi bi-${btn.icon}`}
                                        style={{ fontSize: device.isTablet ? '0.72rem' : '0.8rem' }} />
                                    </button>
                                  </OverlayTrigger>
                                ))}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Table Footer */}
                <div style={{
                  padding: device.isTablet ? '0.65rem 1rem' : '0.75rem 1.25rem',
                  background: theme.bgSecondary,
                  borderTop: `1px solid ${theme.border}`,
                }}>
                  <small style={{ color: theme.muted, fontSize: device.isTablet ? '0.7rem' : '0.78rem' }}>
                    Ibyabonetse: <strong style={{ color: theme.text }}>{filteredData.length}</strong> kuri{' '}
                    <strong style={{ color: theme.text }}>{data.length}</strong>
                  </small>
                </div>
              </div>
            )}

            {/* ═══ PAGINATION ═══ */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: device.isMobile ? '0.5rem' : '0.75rem',
                flexWrap: 'wrap',
                gap: '0.5rem',
                paddingBottom: device.isMobile ? '3rem' : 0,
              }}>
                <small style={{
                  color: theme.muted,
                  fontSize: device.isFoldOuter ? '0.5rem' : device.isMobile ? '0.62rem' : '0.75rem',
                }}>
                  {idxFirst + 1}–{Math.min(idxLast, filteredData.length)} / {filteredData.length}
                </small>
                <Pagination className="mb-0" size={device.isMobile ? 'sm' : undefined}>
                  <Pagination.Prev
                    disabled={currentPage === 1}
                    onClick={() => paginate(currentPage - 1)}
                  />
                  {getPaginationRange().map((p) => (
                    <Pagination.Item
                      key={p}
                      active={p === currentPage}
                      onClick={() => paginate(p)}
                    >
                      {p}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next
                    disabled={currentPage === totalPages}
                    onClick={() => paginate(currentPage + 1)}
                  />
                </Pagination>
              </div>
            )}
          </>
        )}

        {/* ════════════════════════════════════════════
            MOBILE FAB
        ════════════════════════════════════════════ */}
        {device.isMobile && (
          <div
            className="fab-container"
            ref={fabRef}
            style={{ bottom: 20, right: device.isFoldOuter ? 10 : 14 }}
          >
            {showMobileMenu && (
              <div className="fab-menu">
                {[
                  { label: 'Ongeraho', icon: 'plus-circle', color: theme.success, action: () => { resetForm(); setShowForm(true); setShowMobileMenu(false); } },
                  { label: 'Fungura', icon: 'arrow-clockwise', color: theme.info, action: () => { fetchRecords(); setShowMobileMenu(false); } },
                ].map((item, idx) => (
                  <button key={idx} className="fab-menu-item" onClick={item.action}>
                    <i className={`bi bi-${item.icon}`} style={{ color: item.color, fontSize: '1rem' }} />
                    {item.label}
                  </button>
                ))}
              </div>
            )}
            <button
              className="fab-button"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              style={{
                transform: showMobileMenu ? 'rotate(45deg)' : 'none',
                width: device.isFoldOuter ? 46 : 52,
                height: device.isFoldOuter ? 46 : 52,
                fontSize: device.isFoldOuter ? '1.1rem' : '1.3rem',
              }}
            >
              <i className="bi bi-plus-lg" />
            </button>
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════════
          DELETE CONFIRMATION MODAL
      ════════════════════════════════════════════════ */}
      <Modal
        show={showDeleteModal}
        onHide={() => { setShowDeleteModal(false); setItemToDelete(null); }}
        centered
        backdrop="static"
      >
        <div style={{
          background: theme.card,
          borderRadius: device.isMobile ? '20px 20px 0 0' : '20px',
          border: `1px solid ${theme.border}`,
          overflow: 'hidden',
        }}>
          <div style={{ padding: device.isMobile ? '1.5rem' : '2rem', textAlign: 'center' }}>
            <div style={{
              width: device.isMobile ? '65px' : '80px',
              height: device.isMobile ? '65px' : '80px',
              borderRadius: '50%',
              background: 'rgba(255, 61, 113, 0.1)',
              border: `3px solid ${theme.danger}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.2rem',
              animation: 'pulse-danger 2s infinite',
            }}>
              <i className="bi bi-exclamation-triangle-fill" style={{
                fontSize: device.isMobile ? '1.6rem' : '2rem',
                color: theme.danger,
              }} />
            </div>

            <h5 style={{
              color: theme.textBright,
              fontWeight: 700,
              marginBottom: '0.5rem',
              fontSize: device.isMobile ? '1rem' : '1.15rem',
            }}>
              Gusiba Amakuru
            </h5>

            <p style={{ color: theme.muted, fontSize: device.isMobile ? '0.8rem' : '0.9rem', marginBottom: '0.4rem' }}>
              Uremeza ko ushaka gusiba amakuru ya:
            </p>

            {itemToDelete && (
              <div style={{
                background: 'rgba(255, 61, 113, 0.08)',
                border: '1px solid rgba(255, 61, 113, 0.2)',
                borderRadius: '10px',
                padding: '0.6rem 1rem',
                marginBottom: '0.75rem',
              }}>
                <strong style={{
                  color: theme.textBright,
                  fontSize: device.isMobile ? '0.9rem' : '1rem',
                }}>
                  <i className="bi bi-person-fill me-2" style={{ color: theme.danger }} />
                  {itemToDelete.amazina_y_umuturage}
                </strong>
              </div>
            )}

            <p style={{ color: theme.muted, fontSize: device.isMobile ? '0.72rem' : '0.82rem', marginBottom: '1.2rem' }}>
              <i className="bi bi-info-circle me-1" />
              Iki gikorwa ntigishobora gusubizwaho.
            </p>

            <div style={{
              display: 'flex',
              gap: '0.5rem',
              justifyContent: 'center',
              flexDirection: device.isFoldOuter ? 'column' : 'row',
            }}>
              <button
                onClick={() => { setShowDeleteModal(false); setItemToDelete(null); }}
                style={{
                  background: 'transparent',
                  border: `1px solid ${theme.borderLight}`,
                  borderRadius: '10px',
                  color: theme.muted,
                  padding: '0.55rem 1.3rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: device.isMobile ? '0.82rem' : '0.88rem',
                  width: device.isFoldOuter ? '100%' : 'auto',
                }}
              >
                <i className="bi bi-x-circle me-1" />Oya
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  background: `linear-gradient(135deg, ${theme.danger}, #b91c1c)`,
                  border: 'none',
                  borderRadius: '10px',
                  color: '#ffffff',
                  padding: '0.55rem 1.3rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: `0 4px 15px ${theme.dangerGlow}`,
                  fontFamily: 'inherit',
                  fontSize: device.isMobile ? '0.82rem' : '0.88rem',
                  width: device.isFoldOuter ? '100%' : 'auto',
                }}
              >
                <i className="bi bi-trash me-1" />Yego, Siba
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default GutangaUmutekano;